import React, { useContext, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../../auth/AuthContext';
import { NAVBAR_ITEMS } from '../Routes';
import { NavBarItem, isNavBarItemGroup } from '../helpers';
import styles from './NavBar.module.css';
import { makeUserHasGroupAccess } from '../../auth/helpers';
import cn from 'classnames';
import { useGetScreenDimensions } from '../../../hooks/useGetScreenDimensions';
import NavMenu from './components/NavMenu';
import { ReactComponent as ApexLogo } from '../../../assets/ApexLogo.svg';

export function NavBar() {
    const authContext = useContext(AuthContext);
    const userHasGroupAccess = useMemo(
        () => makeUserHasGroupAccess(authContext.user?.groups ?? []),
        [authContext.user?.groups]
    );
    const location = useLocation();
    const title = useMemo(() => {
        const group = NAVBAR_ITEMS.find(
            (item) => isNavBarItemGroup(item) && !!item.items.find((item) => location.pathname.includes(item.url))
        );

        const route = NAVBAR_ITEMS.map((item) => {
            if (isNavBarItemGroup(item)) {
                return item.items;
            }

            return item;
        })
            .flat()
            .find((route) => location.pathname.includes(route.url));

        if (group && route) {
            return `${route.title} | ${group.title} | apex`;
        }

        if (route) {
            return `${route.title} | apex`;
        }

        return 'apex';
    }, [location.pathname]);

    const navBarToDisplay = useMemo(() => {
        if (authContext.loading || !authContext.authenticated) {
            return [];
        }

        return NAVBAR_ITEMS.reduce<NavBarItem[]>((navBar, item) => {
            if (isNavBarItemGroup(item) && item.items.some((item) => userHasGroupAccess(item.groups, 'all'))) {
                item.items = item.items.filter((i) => userHasGroupAccess(i.groups, 'all'));
                navBar.push(item);
            }

            if (!isNavBarItemGroup(item) && userHasGroupAccess(item.groups, 'all')) {
                navBar.push(item);
            }

            return navBar;
        }, []);
    }, [authContext.authenticated, authContext.loading, userHasGroupAccess]);

    const { screenWidth } = useGetScreenDimensions();
    const renderNavMenu = screenWidth < 1024 && !!navBarToDisplay.length;

    return (
        <>
            <Helmet>
                <title>{title}</title>
            </Helmet>
            <div className={styles.navBar}>
                <ApexLogo className={styles.apexLogo} />
                {renderNavMenu ? (
                    <>
                        <div className={cn(styles.navBarSection, styles.stretchSection)}>
                            <NavMenu navItems={navBarToDisplay} />
                        </div>
                    </>
                ) : (
                    <div className={cn(styles.navBarSection, styles.stretchSection)}>
                        <div className={styles.links}>
                            {navBarToDisplay.map((item) => {
                                if (isNavBarItemGroup(item)) {
                                    const activeLink = item.items.find((link) => location.pathname.includes(link.url));

                                    return (
                                        <div key={item.title} className={styles.navItem}>
                                            <span className={cn(styles.label, { [styles.active]: !!activeLink })}>
                                                {item.title}
                                            </span>
                                            <div className={styles.dropdown}>
                                                {item.items.map((link) => (
                                                    <Link key={link.title} to={link.url} className={styles.navItem}>
                                                        <span
                                                            className={cn(styles.label, {
                                                                [styles.active]: activeLink?.url === link.url,
                                                            })}
                                                        >
                                                            {link.title}
                                                        </span>
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                }

                                const isActive = location.pathname.includes(item.url);
                                return (
                                    <Link key={item.title} to={item.url} className={styles.navItem}>
                                        <span className={cn(styles.label, { [styles.active]: isActive })}>
                                            {item.title}
                                        </span>
                                    </Link>
                                );
                            })}
                        </div>
                    </div>
                )}
                <div className={styles.navBarSection}>
                    {!authContext.loading && authContext.authenticated && (
                        <div
                            className={styles.logout}
                            onClick={() => {
                                if (!authContext.loading && authContext.authenticated) {
                                    authContext.logout();
                                }
                            }}
                        >
                            <span>Sign Out</span>
                        </div>
                    )}

                    {!authContext.loading && !authContext.authenticated && (
                        <Link to="/sign-in/" className={styles.logout}>
                            Sign In
                        </Link>
                    )}
                </div>
            </div>
        </>
    );
}
